
import React from 'react';
import { Card } from "@/components/ui/card";
import { ChartBar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { PerformanceData } from './types';
import { useIsMobile } from '@/hooks/use-mobile';
import ResponsiveChartContainer from './ResponsiveChartContainer';
import ResponsiveChartTooltip from './ResponsiveChartTooltip';

interface ScoreDistributionProps {
  performanceData: PerformanceData[];
}

export const ScoreDistribution: React.FC<ScoreDistributionProps> = ({ performanceData }) => {
  const { isMobile } = useIsMobile();
  
  const distributionData = React.useMemo(() => {
    // Create score ranges (0-10, 11-20, etc.)
    const ranges = Array.from({ length: 10 }, (_, i) => ({
      range: `${i * 10}-${(i + 1) * 10}`,
      count: 0,
      start: i * 10,
      end: (i + 1) * 10
    }));

    // Count scores in each range
    performanceData.forEach(data => {
      const score = Math.floor(data.score);
      const rangeIndex = Math.min(Math.floor(score / 10), 9);
      ranges[rangeIndex].count++;
    });

    return ranges;
  }, [performanceData]);

  const maxCount = Math.max(...distributionData.map(d => d.count));
  
  // Create chart config with appropriate colors
  const chartConfig = {
    scores: {
      label: 'Score Distribution',
      color: '#8B5CF6'
    }
  };

  return (
    <Card className={isMobile ? "p-3" : "p-6"}>
      <h2 className={`${isMobile ? "text-base" : "text-xl"} font-semibold mb-4 flex items-center gap-2`}>
        <ChartBar className="w-5 h-5 text-purple-500" />
        Score Distribution
      </h2>
      <div className="w-full">
        <ResponsiveChartContainer 
          config={chartConfig}
          mobileHeight={220}
          desktopHeight={300}
        >
          <BarChart 
            data={distributionData}
            margin={isMobile ? { top: 5, right: 5, bottom: 30, left: 0 } : { top: 10, right: 10, bottom: 20, left: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis 
              dataKey="range"
              angle={isMobile ? -45 : 0}
              tick={{ fontSize: isMobile ? 10 : 12 }}
              interval={isMobile ? 1 : 0} 
              textAnchor={isMobile ? "end" : "middle"}
              height={isMobile ? 60 : 50}
            />
            <YAxis
              width={isMobile ? 30 : 40}
              tick={{ fontSize: isMobile ? 10 : 12 }}
              domain={[0, maxCount + 1]}
            />
            <ResponsiveChartTooltip
              formatter={(value: any) => [String(value), 'Attempts']}
            />
            <Bar
              dataKey="count"
              fill="#8B5CF6"
              radius={[4, 4, 0, 0]}
              name="Attempts"
            />
          </BarChart>
        </ResponsiveChartContainer>
      </div>
      <div className={`mt-2 ${isMobile ? "text-xs" : "text-sm"} text-gray-500`}>
        Distribution of scores across {performanceData.length} quiz attempts
      </div>
    </Card>
  );
};
